'use client';

import { c as _c } from "react/compiler-runtime";
import { CartCurrencyMismatchBox, CartCurrencyProvider } from '@feat/feat-cart/currency';
import { trackViewItem } from '@feat/feat-tracking/view';
import { UrlStateProvider } from '@feat/feat-url-state/provider';
import type { UrlState } from '@feat/feat-url-state/schema';
import type { DehydratedState } from '@tanstack/react-query';
import { HydrationBoundary } from '@tanstack/react-query';
import dynamic from 'next/dynamic';
import type { PropsWithChildren } from 'react';
import { useEffect, useState } from 'react';
import { MerchantProfileLoading } from 'src/app/_page-chrome/merchant-profile-loading';
import { AppAccommodationBookingBox } from 'src/app/unterkuenfte/_components/app-accommodation-booking-box';
import { AppAccommodationBookingBoxProvider } from 'src/app/unterkuenfte/_components/app-accommodation-booking-box-provider';
import { AccommodationDetailSectionLoading, AccommodationImportantNotesLoading, AccommodationTeaserLoading } from 'src/app/unterkuenfte/_components/app-accommodation-deferred-section-loading';
import { AppAccommodationHeader } from 'src/app/unterkuenfte/_components/app-accommodation-header';
import type { AccommodationDetailData } from 'src/app/unterkuenfte/_lib/accommodation-server';
import { HeroSection } from 'src/shared/composites/product-hero';
import { AccommodationBreadcrumbs } from './accommodation-breadcrumbs';
import { AccommodationFacilities } from './accommodation-facilities';
import { AccommodationUnavailableBox } from './accommodation-unavailable-box';
import { AppSingleRoomDetails } from './app-single-room-details';
import { AccommodationReviewSection } from './review/review-section';
const AccommodationLightbox = dynamic(() => import('./accommodation-lightbox').then(mod => mod.AccommodationLightbox), {
  ssr: false
});
const AccommodationMap = dynamic(() => import('@feat/feat-accommodation/map').then(m => m.AccommodationMap), {
  ssr: false,
  loading: () => <div className="pb-5 sm:pb-8">
        <div className="isolate aspect-3/2 overflow-hidden rounded-2xl bg-sand" />
      </div>
});
const AccommodationReviewModal = dynamic(() => import('./review/review-modal').then(mod => mod.AccommodationReviewModal), {
  ssr: false
});
const AccommodationDetailSection = dynamic(() => import('./accommodation-detail-section').then(mod => mod.AccommodationDetailSection), {
  loading: () => <AccommodationDetailSectionLoading />
});
const ADPTeaser = dynamic(() => import('./adp-teaser').then(mod => mod.ADPTeaser), {
  loading: () => <AccommodationTeaserLoading />
});
const AccommodationImportantNotes = dynamic(() => import('./accommodation-important-notes').then(mod => mod.AccommodationImportantNotes), {
  loading: () => <AccommodationImportantNotesLoading />
});
const MerchantProfile = dynamic(() => import('src/features/merchant/merchant-profile').then(mod => mod.MerchantProfile), {
  loading: () => <MerchantProfileLoading />
});
type Accommodation = AccommodationDetailData;
type AccommodationDetailPageClientProps = {
  accommodation: Accommodation;
  initialUrlState: UrlState;
  dehydratedState: DehydratedState;
  cartCurrency: string | null;
};
function Section(t0) {
  const $ = _c(6);
  const {
    children,
    title,
    className,
    hidden
  } = t0;
  if (hidden) {
    return null;
  }
  let t1;
  if ($[0] !== title) {
    t1 = title && <h2 className="mb-5 font-heading2 sm:mb-8">{title}</h2>;
    $[0] = title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== className || $[4] !== t1) {
    t2 = <section className={className}>{t1}{children}</section>;
    $[2] = children;
    $[3] = className;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
}
export function AccommodationDetailPageClient(t0) {
  const $ = _c(11);
  const {
    accommodation,
    initialUrlState,
    dehydratedState,
    cartCurrency
  } = t0;
  let t1;
  if ($[0] !== accommodation) {
    t1 = <AccommodationDetailContent accommodation={accommodation} />;
    $[0] = accommodation;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== cartCurrency || $[3] !== t1) {
    t2 = <CartCurrencyProvider cartCurrency={cartCurrency}>{t1}</CartCurrencyProvider>;
    $[2] = cartCurrency;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== dehydratedState || $[6] !== t2) {
    t3 = <HydrationBoundary state={dehydratedState}>{t2}</HydrationBoundary>;
    $[5] = dehydratedState;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== initialUrlState || $[9] !== t3) {
    t4 = <UrlStateProvider defaultValue={initialUrlState} initialUrlSync={true}>{t3}</UrlStateProvider>;
    $[8] = initialUrlState;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
}
function AccommodationDetailContent(t0) {
  const $ = _c(105);
  const {
    accommodation
  } = t0;
  const [isOpen, setIsOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedReview, setSelectedReview] = useState();
  let t1;
  if ($[0] !== accommodation.foodAndBeverageImages) {
    t1 = accommodation.foodAndBeverageImages ?? [];
    $[0] = accommodation.foodAndBeverageImages;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== accommodation.wellnessImages) {
    t2 = accommodation.wellnessImages ?? [];
    $[2] = accommodation.wellnessImages;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== accommodation.activitiesImages) {
    t3 = accommodation.activitiesImages ?? [];
    $[4] = accommodation.activitiesImages;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== accommodation.images || $[7] !== t1 || $[8] !== t2 || $[9] !== t3) {
    t4 = [...accommodation.images, ...t1, ...t2, ...t3];
    $[6] = accommodation.images;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const images = t4;
  let t5;
  if ($[11] !== images) {
    t5 = (clickedImages, index) => {
      const startAt = images.findIndex(image => image.url === clickedImages[0]?.url);
      setSelectedImageIndex(startAt + index);
      setIsOpen(true);
    };
    $[11] = images;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const handleImageClick = t5;
  let t6;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = id => {
      setSelectedReview(id);
      setShowReviewModal(true);
    };
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const handleShowSeeAllReviews = t6;
  let t7;
  if ($[14] !== accommodation.id || $[15] !== accommodation.merchant.tradeName || $[16] !== accommodation.name || $[17] !== accommodation.zipCode) {
    t7 = () => {
      trackViewItem({
        item_id: `A-${accommodation.id}`,
        item_name: accommodation.name,
        item_category: "accommodation",
        item_brand: accommodation.merchant.tradeName ?? "",
        item_category5: accommodation.zipCode
      });
    };
    $[14] = accommodation.id;
    $[15] = accommodation.merchant.tradeName;
    $[16] = accommodation.name;
    $[17] = accommodation.zipCode;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== accommodation) {
    t8 = [accommodation];
    $[19] = accommodation;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  useEffect(t7, t8);
  let t9;
  if ($[21] !== images) {
    t9 = images.map(_temp);
    $[21] = images;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== accommodation || $[24] !== t9) {
    t10 = <HeroSection images={t9} product={accommodation} />;
    $[23] = accommodation;
    $[24] = t9;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== accommodation.currency) {
    t11 = <CartCurrencyMismatchBox productCurrency={accommodation.currency} productType="accommodation" />;
    $[26] = accommodation.currency;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== accommodation) {
    t12 = <AppAccommodationHeader accommodation={accommodation} onLinkClick={handleShowSeeAllReviews} />;
    $[28] = accommodation;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== t11 || $[31] !== t12) {
    t13 = <Section className="flex flex-col gap-y-4">{t11}{t12}</Section>;
    $[30] = t11;
    $[31] = t12;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] !== accommodation.room) {
    t14 = accommodation.room && <Section title="Die Unterkunft"><AppSingleRoomDetails room={accommodation.room} /></Section>;
    $[33] = accommodation.room;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  let t15;
  if ($[35] !== accommodation) {
    t15 = <Section title="Unterkunftsausstattung"><AccommodationFacilities accommodation={accommodation} /></Section>;
    $[35] = accommodation;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  let t16;
  if ($[37] !== accommodation) {
    t16 = <Section title="Lage und Umgebung"><AccommodationMap accommodation={accommodation} /></Section>;
    $[37] = accommodation;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  const t17 = !accommodation.reviewScores && !accommodation.reviews.length;
  let t18;
  if ($[39] !== accommodation.reviewScores || $[40] !== accommodation.reviews) {
    t18 = <AccommodationReviewSection reviewScores={accommodation.reviewScores} reviews={accommodation.reviews} onSeeAll={handleShowSeeAllReviews} />;
    $[39] = accommodation.reviewScores;
    $[40] = accommodation.reviews;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  let t19;
  if ($[42] !== t17 || $[43] !== t18) {
    t19 = <Section title="Bewertungen" hidden={t17}>{t18}</Section>;
    $[42] = t17;
    $[43] = t18;
    $[44] = t19;
  } else {
    t19 = $[44];
  }
  const t20 = !accommodation.foodAndBeverageHtml;
  let t21;
  if ($[45] !== accommodation.foodAndBeverageHtml || $[46] !== accommodation.foodAndBeverageImages || $[47] !== handleImageClick) {
    t21 = <AccommodationDetailSection html={accommodation.foodAndBeverageHtml} images={accommodation.foodAndBeverageImages} onImageClick={handleImageClick} />;
    $[45] = accommodation.foodAndBeverageHtml;
    $[46] = accommodation.foodAndBeverageImages;
    $[47] = handleImageClick;
    $[48] = t21;
  } else {
    t21 = $[48];
  }
  let t22;
  if ($[49] !== t20 || $[50] !== t21) {
    t22 = <Section title="Essen & Trinken" hidden={t20}>{t21}</Section>;
    $[49] = t20;
    $[50] = t21;
    $[51] = t22;
  } else {
    t22 = $[51];
  }
  const t23 = !accommodation.wellnessHtml;
  let t24;
  if ($[52] !== accommodation.wellnessHtml || $[53] !== accommodation.wellnessImages || $[54] !== handleImageClick) {
    t24 = <AccommodationDetailSection html={accommodation.wellnessHtml} images={accommodation.wellnessImages} onImageClick={handleImageClick} />;
    $[52] = accommodation.wellnessHtml;
    $[53] = accommodation.wellnessImages;
    $[54] = handleImageClick;
    $[55] = t24;
  } else {
    t24 = $[55];
  }
  let t25;
  if ($[56] !== t23 || $[57] !== t24) {
    t25 = <Section title={"Wellness zum Wohlf\xFChlen"} hidden={t23}>{t24}</Section>;
    $[56] = t23;
    $[57] = t24;
    $[58] = t25;
  } else {
    t25 = $[58];
  }
  const t26 = !accommodation.activitiesHtml;
  let t27;
  if ($[59] !== accommodation.activitiesHtml || $[60] !== accommodation.activitiesImages || $[61] !== handleImageClick) {
    t27 = <AccommodationDetailSection html={accommodation.activitiesHtml} images={accommodation.activitiesImages} onImageClick={handleImageClick} />;
    $[59] = accommodation.activitiesHtml;
    $[60] = accommodation.activitiesImages;
    $[61] = handleImageClick;
    $[62] = t27;
  } else {
    t27 = $[62];
  }
  let t28;
  if ($[63] !== t26 || $[64] !== t27) {
    t28 = <Section title={"Sport & Aktivit\xE4ten"} hidden={t26}>{t27}</Section>;
    $[63] = t26;
    $[64] = t27;
    $[65] = t28;
  } else {
    t28 = $[65];
  }
  let t29;
  if ($[66] !== accommodation.id) {
    t29 = <Section title="Lust auf unvergessliche Urlaubsmomente?"><ADPTeaser accommodationId={accommodation.id} /></Section>;
    $[66] = accommodation.id;
    $[67] = t29;
  } else {
    t29 = $[67];
  }
  let t30;
  if ($[68] !== accommodation) {
    t30 = <Section title="Wichtige Informationen"><AccommodationImportantNotes accommodation={accommodation} /></Section>;
    $[68] = accommodation;
    $[69] = t30;
  } else {
    t30 = $[69];
  }
  let t31;
  if ($[70] !== accommodation.merchant) {
    t31 = <Section className="lg:pt-4"><MerchantProfile merchant={accommodation.merchant} /></Section>;
    $[70] = accommodation.merchant;
    $[71] = t31;
  } else {
    t31 = $[71];
  }
  let t32;
  if ($[72] !== t13 || $[73] !== t14 || $[74] !== t15 || $[75] !== t16 || $[76] !== t19 || $[77] !== t22 || $[78] !== t25 || $[79] !== t28 || $[80] !== t29 || $[81] !== t30 || $[82] !== t31) {
    t32 = <div className="col-span-12 sm:col-span-7"><div className="fluid-container w-full max-w-screen divide-y divide-primary/15 *:py-8 *:first:pt-0 *:last:pb-0 sm:divide-y-0 sm:px-0">{t13}{t14}{t15}{t16}{t19}{t22}{t25}{t28}{t29}{t30}{t31}</div></div>;
    $[72] = t13;
    $[73] = t14;
    $[74] = t15;
    $[75] = t16;
    $[76] = t19;
    $[77] = t22;
    $[78] = t25;
    $[79] = t28;
    $[80] = t29;
    $[81] = t30;
    $[82] = t31;
    $[83] = t32;
  } else {
    t32 = $[83];
  }
  let t33;
  if ($[84] !== accommodation) {
    t33 = <div className="sm:col-span-5 sm:col-start-8 md:col-span-4 md:col-start-9">{accommodation.status === "unavailable" ? <AccommodationUnavailableBox accommodation={accommodation} /> : <AppAccommodationBookingBoxProvider><AppAccommodationBookingBox accommodation={accommodation} /></AppAccommodationBookingBoxProvider>}</div>;
    $[84] = accommodation;
    $[85] = t33;
  } else {
    t33 = $[85];
  }
  let t34;
  if ($[86] !== t32 || $[87] !== t33) {
    t34 = <div className="sm:fluid-grid sm:fluid-container grid pt-4 pb-8 sm:pt-12 sm:pb-6 md:pb-8 lg:pb-12">{t32}{t33}</div>;
    $[86] = t32;
    $[87] = t33;
    $[88] = t34;
  } else {
    t34 = $[88];
  }
  let t35;
  if ($[89] !== accommodation) {
    t35 = <AccommodationBreadcrumbs accommodation={accommodation} destination={accommodation.destination} placement="bottom" />;
    $[89] = accommodation;
    $[90] = t35;
  } else {
    t35 = $[90];
  }
  let t36;
  if ($[91] !== images || $[92] !== isOpen || $[93] !== selectedImageIndex) {
    t36 = isOpen && <AccommodationLightbox isOpen={isOpen} onClose={() => setIsOpen(false)} images={images} selectedIndex={selectedImageIndex} />;
    $[91] = images;
    $[92] = isOpen;
    $[93] = selectedImageIndex;
    $[94] = t36;
  } else {
    t36 = $[94];
  }
  let t37;
  if ($[95] !== accommodation || $[96] !== selectedReview || $[97] !== showReviewModal) {
    t37 = showReviewModal && <AccommodationReviewModal accommodation={accommodation} isOpen={showReviewModal} onClose={() => setShowReviewModal(false)} reviewId={selectedReview} />;
    $[95] = accommodation;
    $[96] = selectedReview;
    $[97] = showReviewModal;
    $[98] = t37;
  } else {
    t37 = $[98];
  }
  let t38;
  if ($[99] !== t10 || $[100] !== t34 || $[101] !== t35 || $[102] !== t36 || $[103] !== t37) {
    t38 = <>{t10}{t34}{t35}{t36}{t37}</>;
    $[99] = t10;
    $[100] = t34;
    $[101] = t35;
    $[102] = t36;
    $[103] = t37;
    $[104] = t38;
  } else {
    t38 = $[104];
  }
  return t38;
}
function _temp(image_0) {
  return {
    url: image_0.url,
    alt: "Bild der Unterkunft"
  };
}